"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Lock } from "lucide-react";

const TIERS = [
  { name: "General Admission", capacity: 800, sold: 487, color: "white" },
  { name: "VIP Booth", capacity: 6, sold: 6, color: "pink", soldOut: true },
  { name: "Bottle Service", capacity: 12, sold: 12, color: "amber", soldOut: true },
];

export default function EventTicketingSelloutScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0F0B1F] text-white overflow-hidden">
      <div
        className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
                {t("Capacity is law", "El aforo es la ley")}
              </div>
              <h2 className="text-4xl md:text-[56px] font-black tracking-[-0.03em] leading-[1.02] mb-6">
                {t("Sell out. ", "Sold out. ")}
                <br />
                <span className="text-white/30">{t("Auto-close.", "Cierre automático.")}</span>
              </h2>
              <p className="text-[16px] font-medium text-white/55 leading-relaxed">
                {t(
                  "Set capacity per tier. The minute the last VIP booth is gone, that tier locks. No oversold, no \"sorry we have to refund 4 people.\"",
                  "Defines aforo por tier. En cuanto se vende el último VIP, ese tier se cierra. Sin sobreventa, sin \"perdón, tenemos que reembolsar a 4 personas\"."
                )}
              </p>
            </motion.div>
          </div>

          {/* Tier cards */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="space-y-4"
            >
              {TIERS.map((tier, i) => {
                const pct = (tier.sold / tier.capacity) * 100;
                return (
                  <motion.div
                    key={tier.name}
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5, delay: 0.15 * i }}
                    className="relative bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[18px] p-5 overflow-hidden"
                  >
                    {tier.soldOut && (
                      <motion.div
                        initial={{ opacity: 0, scale: 1.2, rotate: -8 }}
                        whileInView={{ opacity: 1, scale: 1, rotate: -6 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.4, delay: 0.4 + 0.15 * i }}
                        className="absolute top-4 right-5 px-3 py-1.5 rounded text-[11px] font-black tracking-widest uppercase border-2"
                        style={{
                          borderColor:
                            tier.color === "pink" ? "#F472B6" : "#FB923C",
                          color:
                            tier.color === "pink" ? "#F472B6" : "#FB923C",
                          backgroundColor: "rgba(0,0,0,0.4)",
                        }}
                      >
                        {t("SOLD OUT", "AGOTADO")}
                      </motion.div>
                    )}

                    <div className="flex items-baseline justify-between mb-2 pr-24">
                      <div className="text-[16px] font-black text-white">{tier.name}</div>
                    </div>
                    <div className="flex items-baseline gap-2 mb-3">
                      <div className="text-[13px] font-bold text-white/70 tabular-nums">
                        {tier.sold}
                      </div>
                      <div className="text-[11px] text-white/40">/</div>
                      <div className="text-[11px] text-white/40 tabular-nums">{tier.capacity}</div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-white/40 ml-2">
                        {t("sold", "vendido")}
                      </div>
                      {tier.soldOut && (
                        <div className="ml-auto flex items-center gap-1 text-[10px] font-bold text-white/40">
                          <Lock className="w-3 h-3" />
                          {t("Locked", "Bloqueado")}
                        </div>
                      )}
                    </div>

                    {/* Progress bar */}
                    <div className="relative h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: "0%" }}
                        whileInView={{ width: `${pct}%` }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{
                          duration: 1.2,
                          delay: 0.2 + 0.15 * i,
                          ease: [0.21, 0.47, 0.32, 0.98],
                        }}
                        className="absolute inset-y-0 left-0 rounded-full"
                        style={{
                          background: tier.soldOut
                            ? tier.color === "pink"
                              ? "linear-gradient(90deg, #7C3AED 0%, #F472B6 100%)"
                              : "linear-gradient(90deg, #F472B6 0%, #FB923C 100%)"
                            : "linear-gradient(90deg, #7C3AED 0%, #C4B5FD 100%)",
                        }}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
